import * as React from 'react';

import { Popover as HeadlessPopover } from '@headlessui/react';

import { ChevronDown } from '@freelancelabs/icons';
import { clsx } from '@freelancelabs/utils';

import { generateUID } from '../../../helpers';
import { useInstance } from '../../../hooks';
import { CheckboxGroup, Input, RadioGroup } from '../../atoms/input';
import { PopoverPanel } from '../../atoms/popover';

export interface FilterPillOption<T extends string> {
    value: T;
    label: string;
    /**
     * A choice the reader may not make. **Kept in the list and greyed, never removed**
     * (PCG-135): a menu quietly nineteen rows long where the contract has twenty reads
     * as an incomplete list, and « vous ne pouvez pas décocher celle-ci » is not
     * « celle-ci n'existe pas ».
     *
     * `single` ignores it beyond the native attribute: a radio group answers exactly
     * one value and has no state to protect.
     */
    disabled?: boolean;
    /**
     * Why this one cannot be ticked or unticked. Same reading as `SelectOption` and
     * `TreeNode`: the **short qualifier** that answers « pourquoi celle-là », rendered
     * beside the label rather than only announced. The rule behind a whole family of
     * refusals goes in `menuTitle`, said once.
     */
    disabledReason?: React.ReactNode;
    /**
     * What the menu's own search matches, when the label is not enough.
     *
     * Defaults to `label`. An operational contact is offered by name and **found by
     * name or by address**: two people share a name, their addresses do not, and the
     * address is what tells them apart in a menu hundreds of rows long. Passing it
     * here rather than lengthening the label keeps the row readable.
     */
    searchText?: string;
}

/**
 * **Attention, the value is a string, and that is a constraint rather than a convenience.**
 * In `single` mode the choice round-trips through the DOM, a radio answering
 * `event.target.value`, so anything else would come back as its own `toString()`
 * and no longer compare equal to the option it came from. Callers filter on uuids
 * and on enum members, both of which are strings.
 */
export interface FilterPillProps<T extends string> {
    /** Put on the **button**, so a `<label htmlFor>` or an `aria-labelledby` can aim at it. */
    id?: string;
    /** The name of the criterion: « Entité », « Statut ». It is the button's accessible name. */
    label: string;
    /**
     * Given, the trigger shows THIS and the label goes to `sr-only`.
     *
     * For the one control of the bar that names no criterion: the charter puts the
     * column configurator « en icône seule, poussé à droite ». The label still has to
     * exist and to be read out, a button whose whole content is a decorative glyph
     * having no accessible name at all.
     */
    icon?: React.ReactNode;
    /**
     * `multiple` renders checkboxes and answers every value still ticked; `single`
     * renders radios and answers **exactly one**, because a radio cannot be
     * unticked. A caller of `single` therefore never receives an empty array.
     */
    mode?: 'multiple' | 'single';
    options: FilterPillOption<T>[];
    values: T[];
    onValues: (values: T[]) => void;
    /**
     * The badge beside the label, hidden when null or undefined.
     *
     * **The caller writes it, and the caller decides what counts.** `values.length`
     * is not always the answer: a menu whose first option means « no constraint »
     * (« Depuis le début ») has one value selected and zero criteria applied, and
     * a badge reading « 1 » there would announce a filter nobody set.
     */
    count?: React.ReactNode;
    /**
     * A line above the options, naming what ticking one **does**.
     *
     * **It is what tells two opposite readings of one widget apart** (PCG-135). On a
     * filter, a ticked box narrows the list; on the column configurator of « Mon
     * activité », a ticked box *shows* something. Nothing in the markup distinguishes
     * them, so a pill sitting in a row of filters and meaning the opposite of its
     * neighbours has to say so. It is also where the rule behind a family of locked
     * options is said, once, rather than repeated on every row.
     */
    menuTitle?: React.ReactNode;
    /**
     * Said when `options` is empty. **A menu with nothing in it opens as a blank
     * sliver with nothing in the DOM**, which reads as a broken control rather than
     * as an empty one, so the reason belongs here rather than in a shrug.
     */
    emptyLabel?: React.ReactNode;
    /**
     * The placeholder of the menu's own search field, and its accessible name.
     *
     * **The field appears on its own, past a threshold this component owns** rather
     * than on a flag the caller sets: what makes a search necessary is the NUMBER of
     * options, which is known here. Leaving it to callers would give two menus of the
     * same length two different controls.
     *
     * Given nothing, a generic « Rechercher » is used. Say what is being searched when
     * the criterion is not obvious from the pill's own label.
     */
    searchLabel?: string;
    /**
     * Said when the search matches nothing.
     *
     * **A different fact from `emptyLabel`, and it needs different words**: one says
     * the criterion has nothing to offer at all, the other that this reader's typing
     * matched none of what it offers. Rendering the first for the second sends someone
     * away believing the filter is broken.
     */
    noMatchLabel?: React.ReactNode;
    className?: string;
}

/**
 * Past how many options the menu gets its own search field.
 *
 * **Twelve, and the number is chosen so that no existing menu changes.** The longest
 * pills of the portal today are the column configurators, at ten. What made a search
 * necessary is the « Opérationnel » filter of « Mes besoins » (PCG-170): measured per
 * reader, five people would see two to five options and one would see **395** over
 * their 1 200 needs. A checkbox list is perfect at four and unusable at 395, and no
 * single control serves both without this.
 */
const SEARCH_FROM = 12;

/**
 * Accent-insensitive, case-insensitive comparison of a menu row against what was typed.
 *
 * **It folds accents where the bar's own search field does NOT**, and that asymmetry is
 * deliberate rather than an oversight. The list's search runs in SQL, where `ILIKE`
 * folds case but not accents, and lifting it there needs the `unaccent` extension,
 * a migration, a model decision. Here the whole set is in hand, so folding costs a
 * `normalize` and « berest » finds « Bérest ». The two are not the same search: this one
 * picks a value in a closed menu, that one queries the database.
 */
const folded = (value: string) =>
    value
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase();

/**
 * A filter of a list bar: a pill that unfolds a short menu of options, with a badge
 * counting what is selected. The V2 mockup's `.f-pill` / `.f-menu` / `.f-count`,
 * for the bar of « Mon activité ».
 *
 * **It holds no value and applies on the click, with no « Valider ».** The
 * `Filter` organism of this package does the opposite, a pending selection behind a
 * validate button, and the two are not the same gesture: this one belongs to a bar
 * whose every other control (a search field, a « Réinitialiser ») answers
 * immediately, and one control of a row waiting for a second click reads as one that
 * did not register the first.
 *
 * **Built on `Popover` rather than on `<details>`**, which is what the mockup uses:
 * a `<details>` gives the fold for free and nothing else, and the mockup pays for it
 * with hand-written listeners for the outside click and for Escape. The panel is
 * also floated in a portal here, so a menu opening near the bottom of the bar is not
 * clipped by whatever the bar sits in.
 *
 * **The trigger is a real `<button>`** and not a `<div>` wrapping one, so the
 * `aria-expanded` headlessui puts on it lands on the element that actually takes the
 * focus.
 *
 * **The badge counts what the pill is TAKING AWAY**, whichever payload it carries:
 * criteria applied on a filter, columns hidden on a configurator. That is what keeps
 * one reading across a bar where a ticked box means « narrow to this » on four pills
 * and « show this » on the fifth.
 */
const FilterPill = <T extends string>({
    id,
    label,
    icon,
    mode = 'multiple',
    options,
    values,
    onValues,
    count,
    menuTitle,
    emptyLabel,
    searchLabel,
    noMatchLabel,
    className,
}: FilterPillProps<T>) => {
    const [term, setTerm] = React.useState('');
    const anchorRef = React.useRef<HTMLButtonElement>(null);
    // Native radios group by `name`, and that grouping is what makes the arrow keys
    // walk the options rather than the whole page. `RadioGroup` numbers its ids but
    // sets no name, so two pills on one bar would otherwise share one group.
    const groupName = useInstance(() => generateUID('filter-pill'));

    /**
     * A locked option keeps **exactly** the state it had, whatever the group answers.
     *
     * A disabled `<input>` fires no change event, so this never runs today. It is
     * carried all the same, as the V2 mockup carries its own `VERROUILLEES` guard
     * beside the `disabled` attribute: the refusal is then a property of the pill
     * rather than of one attribute on one element, and a group that ever stops
     * forwarding `disabled` loses the grey, not the rule.
     */
    const isLocked = (value: T) => options.some((option) => option.value === value && option.disabled);
    const searchable = options.length >= SEARCH_FROM;
    const needle = searchable ? folded(term.trim()) : '';
    /**
     * What the menu shows: the rows that match, **plus every row already ticked and
     * every locked row, always**.
     *
     * A selected option hidden by the search is still applied while no longer being
     * on screen: the reader sees a badge counting three criteria, two rows, and has
     * no way to untick the third. Same rule as the tree of the onboarding journey,
     * where a ticked line is never hidden either. The order is kept as the caller
     * sent it, so a row does not jump to the top on being ticked.
     */
    const shown = React.useMemo(() => {
        if (!needle) {
            return options;
        }
        return options.filter(
            (option) =>
                values.includes(option.value) ||
                option.disabled ||
                folded(option.searchText ?? option.label).includes(needle)
        );
    }, [needle, options, values]);

    const onCheckboxes = (next: T[]) => {
        // Empty when nothing is locked, so what goes out is `next` untouched and no
        // existing caller changes behaviour. A locked value that was NOT selected is
        // dropped by the filter, so the guard holds in both directions.
        const locked = values.filter(isLocked);
        onValues([...next.filter((value) => !isLocked(value)), ...locked]);
    };

    return (
        <HeadlessPopover className={clsx('fl-filter-pill', className)}>
            {({ open }) => (
                <>
                    <HeadlessPopover.Button
                        ref={anchorRef}
                        id={id}
                        className={clsx(
                            'fl-filter-pill__button',
                            // The mockup's `.a-actif`: a pill that is narrowing the list
                            // says so while folded, otherwise the only trace of an active
                            // filter is a badge the reader has to go looking for.
                            (open || count != null) && 'fl-filter-pill__button--active'
                        )}
                    >
                        {icon ? (
                            <>
                                {icon}
                                <span className="sr-only">{label}</span>
                            </>
                        ) : (
                            <span className="fl-filter-pill__label">{label}</span>
                        )}
                        {count != null && (
                            // Decorative: the button is already named by its label, and
                            // « Entité 2 » read out is not a sentence. What the badge
                            // stands for is legible from the menu it opens.
                            <span className="fl-filter-pill__count" aria-hidden="true">
                                {count}
                            </span>
                        )}
                        <ChevronDown className="fl-filter-pill__chevron" />
                    </HeadlessPopover.Button>

                    <PopoverPanel anchorRef={anchorRef} isOpen={open}>
                        <div className="fl-filter-pill__menu">
                            {/* Above the empty case too: « Colonnes à afficher » over
                                « rien à proposer » still says what the menu is for,
                                where the sentence alone leaves the reader guessing
                                what it is they have none of. */}
                            {menuTitle != null && <p className="fl-filter-pill__title">{menuTitle}</p>}
                            {searchable && (
                                /* Le champ est HORS du defileur : pose dedans, il
                                   sortirait de l'ecran des la premiere molette, et
                                   c'est precisement quand la liste est longue qu'on
                                   en a besoin. */
                                <Input
                                    className="fl-filter-pill__search"
                                    type="search"
                                    value={term}
                                    onValue={setTerm}
                                    placeholder={searchLabel}
                                    aria-label={searchLabel}
                                />
                            )}
                            {options.length === 0 ? (
                                <p className="fl-filter-pill__empty">{emptyLabel}</p>
                            ) : shown.length === 0 ? (
                                <p className="fl-filter-pill__empty">{noMatchLabel}</p>
                            ) : mode === 'single' ? (
                                <div className="fl-filter-pill__options">
                                    <RadioGroup
                                        name={groupName}
                                        options={shown}
                                        value={values[0]}
                                        onValue={(value) => onValues([value])}
                                    />
                                </div>
                            ) : (
                                <div className="fl-filter-pill__options">
                                    <CheckboxGroup
                                        options={shown.map((option) => ({
                                            value: option.value,
                                            disabled: option.disabled,
                                            // `children` rather than `label`, which is what
                                            // lets the qualifier sit on the row: `Checkbox`
                                            // renders one or the other, so the label keeps
                                            // its own class here rather than losing it.
                                            children: (
                                                <>
                                                    <span className="checkbox-label">{option.label}</span>
                                                    {option.disabledReason != null && (
                                                        <span className="fl-filter-pill__reason">
                                                            {option.disabledReason}
                                                        </span>
                                                    )}
                                                </>
                                            ),
                                        }))}
                                        values={values}
                                        onChange={onCheckboxes}
                                    />
                                </div>
                            )}
                        </div>
                    </PopoverPanel>
                </>
            )}
        </HeadlessPopover>
    );
};

export default FilterPill;
